/**
 * Utility functions for handling profile images
 * Provides proxy URL to avoid Google 429 rate limiting
 */

interface User {
  picture?: string | null
  name?: string
  email?: string
}

/**
 * Get the proxied profile image URL for a user
 * Falls back to null if no picture is available
 * 
 * @param user - User object with optional picture field
 * @returns Proxied image URL or null
 */
export function getProfileImageUrl(user: User | null | undefined): string | undefined {
  if (!user?.picture) {
    return undefined
  }

  // Check if the picture is from Google
  if (!user.picture.includes('googleusercontent.com')) {
    // Non-Google images can be used directly
    return user.picture
  }

  // Use our proxy endpoint to avoid 429 errors
  const encodedUrl = encodeURIComponent(user.picture)
  return `/api/users/profile-image?url=${encodedUrl}`
}

/**
 * Get initials from user name or email
 * Used as fallback when profile image is not available
 * 
 * @param user - User object
 * @returns User initials (1-2 characters)
 */
export function getUserInitials(user: User | null | undefined): string {
  if (!user) return '?'
  
  if (user.name) {
    const parts = user.name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    }
    return user.name.charAt(0).toUpperCase()
  }
  
  if (user.email) {
    return user.email.charAt(0).toUpperCase()
  }
  
  return '?'
}